/**
 * Sign in with a Runebound account (docs/design/07 §2): email and password, or Google / Apple through an
 * OAuth redirect (play.runebound.gg/** is on the allow list). Same accounts as runebound.gg; the session
 * lives on this origin only.
 */
import { Frame, GhostButton, LOOK, Notice, PoroIcon, PrimaryButton, cn, inputClass, inputStyle } from '@play/ui';
import { LogIn, Mail, Swords } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { hasBackend } from '../lib/env';
import { getSupabase } from '../lib/supabase';
import { SESSION_EXPIRED_MESSAGE, useSessionExpiry } from './session-expiry';

type Mode = 'signIn' | 'signUp';

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.37 12.62c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.86-.76-1.47.02-2.83.86-3.59 2.17-1.53 2.65-.39 6.58 1.1 8.73.73 1.05 1.6 2.23 2.73 2.19 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.4 1.2-2.46-.03-.01-2.29-.88-2.3-3.48zM14.2 6.16c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.68-.92 2.67.97.08 1.96-.49 2.56-1.22z" />
    </svg>
  );
}

/** The sign-in panel. `next` is where to come back after an OAuth redirect. */
export function SignInPanel({
  next,
  className,
  autoFocus,
}: {
  next?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);
  const expired = useSessionExpiry((s) => s.expired);

  const redirectTo = `${window.location.origin}${next ?? window.location.pathname + window.location.search}`;

  async function submit(e: { preventDefault: () => void }) {
    e.preventDefault();
    const sb = getSupabase();
    if (!sb) return;
    setBusy(true);
    setMessage(null);
    const res =
      mode === 'signIn'
        ? await sb.auth.signInWithPassword({ email, password })
        : await sb.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo } });
    setBusy(false);
    if (res.error) setMessage({ tone: 'error', text: res.error.message });
    else if (mode === 'signUp' && !res.data.session)
      setMessage({ tone: 'info', text: 'Check your inbox to confirm your email, then sign in.' });
  }

  async function oauth(provider: 'google' | 'apple') {
    const sb = getSupabase();
    if (!sb) return;
    setBusy(true);
    const { error } = await sb.auth.signInWithOAuth({ provider, options: { redirectTo } });
    if (error) {
      setBusy(false);
      setMessage({ tone: 'error', text: error.message });
    }
  }

  if (!hasBackend) {
    return (
      <Frame className={className} title="Sign in unavailable" icon={<LogIn size={15} color="#b9adff" />}>
        <p className="p-4 text-[13px]" style={{ color: LOOK.sub }}>
          This build has no Runebound backend configured (VITE_SUPABASE_URL). The developer sandbox still works.
        </p>
      </Frame>
    );
  }

  return (
    <div id="sign-in" className={cn('scroll-mt-24', className)}>
      <Frame
        title={
          <span id="signin-title">{mode === 'signIn' ? 'Sign in with Runebound' : 'Create a Runebound account'}</span>
        }
        icon={<PoroIcon size={17} className="text-[#b9adff]" />}
      >
        <section className="p-5 flex flex-col gap-4" aria-labelledby="signin-title">
          <p className="text-[13px] -mt-1" style={{ color: LOOK.sub }}>
            Your runebound.gg account and decks.
          </p>
          {expired ? (
            <Notice tone="warn" role="status" data-testid="session-expired">
              {SESSION_EXPIRED_MESSAGE}
            </Notice>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <GhostButton onClick={() => void oauth('google')} disabled={busy}>
              <GoogleMark /> Google
            </GhostButton>
            <GhostButton onClick={() => void oauth('apple')} disabled={busy}>
              <AppleMark /> Apple
            </GhostButton>
          </div>
          <div className="flex items-center gap-3 text-[11.5px]" style={{ color: LOOK.faint }}>
            <span className="h-px flex-1" style={{ background: LOOK.border }} />
            or with email
            <span className="h-px flex-1" style={{ background: LOOK.border }} />
          </div>
          <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
            <label className="flex flex-col gap-1">
              <span className="text-[11.5px]" style={{ color: LOOK.muted }}>
                Email
              </span>
              <input
                autoFocus={autoFocus}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                className={inputClass}
                style={inputStyle}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11.5px]" style={{ color: LOOK.muted }}>
                Password
              </span>
              <input
                type="password"
                required
                minLength={6}
                autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                className={inputClass}
                style={inputStyle}
              />
            </label>
            {message ? (
              <p
                role={message.tone === 'error' ? 'alert' : 'status'}
                className="text-[12.5px]"
                style={{ color: message.tone === 'error' ? LOOK.bad : LOOK.sub }}
              >
                {message.text}
              </p>
            ) : null}
            <PrimaryButton type="submit" disabled={busy} className="mt-1 w-full">
              <Mail size={15} />
              {mode === 'signIn' ? 'Sign in' : 'Create account'}
            </PrimaryButton>
          </form>
          <button
            type="button"
            className="text-[12.5px] hover:underline self-center"
            style={{ color: '#b9adff' }}
            onClick={() => {
              setMode(mode === 'signIn' ? 'signUp' : 'signIn');
              setMessage(null);
            }}
          >
            {mode === 'signIn' ? 'New to Runebound? Create an account' : 'Already have an account? Sign in'}
          </button>
        </section>
      </Frame>
    </div>
  );
}

/** A signed-out page's way in: what signing in is for, then the sign-in panel (`/play`, a room link). */
export function SignInGate({ next, title, children }: { next: string; title: string; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-[420px] pt-14 flex flex-col gap-6">
      <div className="flex flex-col items-center text-center gap-2">
        <span
          className="w-11 h-11 rounded-[11px] inline-flex items-center justify-center mb-1"
          style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
        >
          <Swords size={20} />
        </span>
        <h1 className="font-display text-[26px] leading-8 font-bold" style={{ color: LOOK.text }}>
          {title}
        </h1>
        <p className="text-[13.5px] leading-5" style={{ color: LOOK.sub }}>
          {children}
        </p>
      </div>
      <SignInPanel next={next} />
    </div>
  );
}
